/**
 * Contract DTO - Chuẩn hóa dữ liệu import hợp đồng
 * Đồng bộ với backend Laravel/NestJS
 */

import { ContractTypeEnum } from '@/core/enums/contract.enum';

/**
 * Input DTO - Chuẩn hóa dữ liệu gửi lên API import
 * @param {Array} csvData - Dữ liệu từ CSV
 * @returns {Array} Dữ liệu đã chuẩn hóa cho API
 */
export function ContractImportInputDTO(csvData) {
  if (!Array.isArray(csvData)) {
    return [];
  }

  return csvData.map((row, index) => {
    if (!row || typeof row !== 'object') {
      return null;
    }

    // Helper function to parse integer safely (keep original value if invalid)
    const parseIntegerSafely = (value) => {
      if (!value || value === '') return null;
      const parsed = parseInt(value);
      return isNaN(parsed) ? value : parsed; // Return original value if not a number
    };

    // Helper function to parse integer for API (convert to 0 if invalid)
    const parseIntegerForAPI = (value) => {
      if (!value || value === '') return 0;
      const parsed = parseInt(value);
      return isNaN(parsed) ? 0 : parsed;
    };

    // Helper function to ensure string format (keep leading zeros)
    const ensureString = (value) => {
      return value ? value.toString() : '';
    };

    // Helper function to parse comma-separated string to array (keep original values for validation)
    const parseCommaSeparatedIds = (value) => {
      if (!value || value === '') return [];
      return value.toString().split(',').map(id => id.trim()).filter(id => id !== '');
    };

    return {
      rowIndex: index + 1,
      // Map CSV columns to contract fields - map theo header CSV
      customer_name: ensureString(row['Họ và tên'] || row['ho_va_ten'] || ''),
      customer_email: ensureString(row['Email'] || row['email'] || ''),
      customer_phone: ensureString(row['Số điện thoại'] || row['so_dien_thoai'] || ''),
      customer_birthday: ensureString(row['Ngày sinh'] || row['ngay_sinh'] || ''),
      customer_address: ensureString(row['Địa chỉ'] || row['dia_chi'] || ''),
      customer_cccd: ensureString(row['CCCD'] || row['cccd'] || ''),
      center_id: parseIntegerSafely(row['Trung tâm'] || row['trung_tam']),
      branch_id: parseIntegerSafely(row['Chi nhánh'] || row['chi_nhanh']),
      course_ids: parseCommaSeparatedIds(row['Khóa học'] || row['khoa_hoc']),
      tuition_payment_id: parseIntegerSafely(row['Hình thức đóng học phí'] || row['hinh_thuc_dong_hoc_phi']),
      tvv_id: parseIntegerSafely(row['Mã tư vấn'] || row['ma_tu_van']),
      user_intro_id: parseIntegerSafely(row['Mã giới thiệu'] || row['ma_gioi_thieu']),
      promotion_ids: parseCommaSeparatedIds(row['Mã khuyến mại'] || row['ma_khuyen_mai']),
      type: ContractTypeEnum.STANDARD // Sử dụng enum thay vì hardcode
    };
  }).filter(row => row !== null);
}

/**
 * Output DTO - Chuẩn hóa response từ API import
 * @param {Object} apiResponse - Response từ API
 * @returns {Object} Dữ liệu đã chuẩn hóa
 */
export function ContractImportOutputDTO(apiResponse) {
  return {
    success: apiResponse.success || false,
    message: apiResponse.message || '',
    total: apiResponse.total || 0,
    successCount: apiResponse.success_count || 0,
    errorCount: apiResponse.error_count || 0,
    errors: apiResponse.errors || [],
    results: apiResponse.results || []
  };
}

/**
 * Convert preview data to API format
 * @param {Array} previewData - Dữ liệu preview (giữ nguyên giá trị gốc)
 * @returns {Array} Dữ liệu đã convert cho API
 */
export function convertPreviewToAPIData(previewData) {
  if (!Array.isArray(previewData)) {
    return [];
  }

  return previewData.map(row => {
    if (!row || typeof row !== 'object') {
      return null;
    }

    // Helper function to parse integer for API (convert to 0 if invalid)
    const parseIntegerForAPI = (value) => {
      if (!value || value === '') return 0;
      const parsed = parseInt(value);
      return isNaN(parsed) ? 0 : parsed;
    };

    // Helper function to convert comma-separated string to array of integers (for API)
    const parseCommaSeparatedIdsForAPI = (value) => {
      if (!value || value === '') return [];
      return value.toString().split(',').map(id => parseInt(id.trim())).filter(id => !isNaN(id));
    };

    return {
      rowIndex: row.rowIndex,
      customer_name: row.customer_name || '',
      customer_email: row.customer_email || '',
      customer_phone: row.customer_phone || '',
      customer_birthday: row.customer_birthday || '',
      customer_address: row.customer_address || '',
      customer_cccd: row.customer_cccd || '',
      center_id: parseIntegerForAPI(row.center_id),
      branch_id: parseIntegerForAPI(row.branch_id),
      course_ids: parseCommaSeparatedIdsForAPI(row.course_ids),
      tuition_payment_id: parseIntegerForAPI(row.tuition_payment_id),
      tvv_id: parseIntegerForAPI(row.tvv_id),
      user_intro_id: parseIntegerForAPI(row.user_intro_id),
      promotion_ids: parseCommaSeparatedIdsForAPI(row.promotion_ids),
      type: row.type || ContractTypeEnum.STANDARD
    };
  }).filter(row => row !== null);
}

/**
 * Validation Error DTO - Chuẩn hóa lỗi validation
 * @param {Object} error - Lỗi từ API hoặc validation
 * @returns {Object} Lỗi đã chuẩn hóa
 */
export function ContractValidationErrorDTO(error) {
  return {
    row: error.row || error.rowIndex || 0,
    field: error.field || '',
    message: error.message || '',
    code: error.code || ''
  };
}
